// src/oura/visibility/questionTrend.ts
//
// Whether a question's engine verdict moved since an earlier scan that asked
// THE SAME QUESTION (#4125).
//
// The comparison is per question and per engine, against the newest earlier
// scan that measured that question on that engine, however many scans back and
// whatever the rest of its question set was: the same question text is the
// same measurement. What breaks it is a change of ChatGPT instrument (the app
// and the API read differently), so that engine only compares with scans that
// used the SAME instrument, and an unknown instrument on either side is
// treated as different, never assumed equal.
//
// An unmeasured cell on either side is unknown, not a miss, and a question
// that no loaded scan asked has nothing to compare with: no marker at all,
// never a flat one.

import { dayMonth } from "./format";
import type { VisEngine, VisRow } from "./model";
import type { TrendScan } from "./useQuestionTrendScans";

export interface CellTrend {
    dir: "up" | "down";
    /** What happened, first line of the tooltip: "ChatGPT: named now". */
    headline: string;
    /** Against which scan, and who was named instead on a drop. */
    detail: string;
    /** Why a flip may not mean much. */
    caveat: string;
    /** All of it in one string, for screen readers. */
    title: string;
}

const CAVEAT = "AI answers vary between runs.";

function trendOf(dir: "up" | "down", headline: string, detail: string): CellTrend {
    return { dir, headline, detail, caveat: CAVEAT, title: `${headline}. ${detail} ${CAVEAT}` };
}

export function cellTrend(
    row: VisRow,
    engine: VisEngine,
    engineLabel: string,
    scans: TrendScan[],
    nowInstrument: string | null,
): CellTrend | null {
    const now = row.cells[engine];
    if (!now || (engine === "ai_overview" && row.googlePending)) return null;

    for (const scan of scans) {
        if (engine === "chatgpt" && (!nowInstrument || scan.instrument !== nowInstrument)) continue;
        const before = scan.prior.cells?.[row.key]?.[engine];
        // Not asked on that scan, or not measured there: look further back.
        if (!before) continue;

        const day = dayMonth(scan.prior.capturedAt);
        const when = day ? `on ${day}` : "on an earlier scan";
        if (!before.named && now.named) {
            return trendOf("up", `${engineLabel}: named now`, `Not named ${when}.`);
        }
        if (before.named && !now.named) {
            const instead = now.rivals.slice(0, 2).join(", ");
            return trendOf(
                "down",
                `${engineLabel}: not named now`,
                `Named ${when}.${instead ? ` Named instead: ${instead}.` : ""}`,
            );
        }
        // Measured there and unchanged: that is the answer, older scans do not overrule it.
        return null;
    }
    return null;
}

/**
 * Whether a question is NEW on this scan: the previous scan did not ask it.
 *
 * Compared with the newest earlier scan only, the one the Named over time
 * line connects to. A question asked two scans ago, left out of the previous
 * one and back now IS new against the previous scan, which is what "new" says
 * on the chart too.
 *
 * Returns the day of that scan ("2 Oct"), or the words "an earlier scan" when
 * it carries no date. Null means "not new" AND "cannot tell", on purpose: with
 * no earlier scan loaded (a first scan, still loading, failed), or one that
 * carries no per-question detail, there is nothing to be new against, and a
 * "New" on every row would say something the data does not.
 */
export function newSince(row: VisRow, scans: TrendScan[]): string | null {
    const previous = scans[0];
    if (!previous?.prior.cells) return null;
    if (previous.prior.cells[row.key]) return null;
    return dayMonth(previous.prior.capturedAt) || "an earlier scan";
}

/**
 * The rows' "new since" labels, or null for all of them when EVERY question is
 * new: a whole set that changed has no contrast to mark, and fifty identical
 * tags are noise (the set-changed case is already visible as no trend arrows).
 */
export function newSinceFor(rows: VisRow[], scans: TrendScan[]): ((row: VisRow) => string | null) | null {
    if (scans.length === 0 || rows.length === 0) return null;
    if (rows.every((r) => newSince(r, scans) !== null)) return null;
    return (row) => newSince(row, scans);
}
